/**
 * Truthful recording status for a mounted v2 session — derived from the
 * same coverage fields the player's poll-stop logic already reads
 * (`is_active`, `snapshot_complete`), never a new field or endpoint.
 *
 * `is_active` false does not by itself mean the footage is done: queued
 * uploads can still be draining, so the finalized snapshot may keep
 * gaining media after the phone stops. `snapshot_complete` is what
 * actually answers "is there anything left to heal".
 */
export type RecordingStatus = 'recording' | 'recovering' | 'complete';

export interface RecordingStatusCoverage {
  is_active?: boolean;
  snapshot_complete?: boolean;
}

/**
 * `null` means no truthful verdict is available — either no coverage has
 * arrived yet, or (an older backend) it never reported `is_active` at all.
 * Unlike `FeedPlayer`'s own `live`/VOD derivation, which must pick a posture
 * either way and so defaults a missing flag to "still live", this status is
 * shown as a claim to the viewer — asserting "Recording" without truthful
 * evidence would repeat the exact bug this exists to fix. The UI falls back
 * to its own pre-existing label instead of trusting a guess here.
 */
export function deriveRecordingStatus(
  coverage: RecordingStatusCoverage | null,
): RecordingStatus | null {
  if (!coverage || coverage.is_active == null) return null;
  if (coverage.is_active) return 'recording';
  return coverage.snapshot_complete ? 'complete' : 'recovering';
}

/**
 * The exact label a host renders for the mounted session. Prefers the
 * truthful `status` (from coverage) when known; falls back to the
 * SSE-derived `fallbackRecording` boolean only while `status` is null (no
 * coverage verdict has arrived) — the two are never blended, so a truthful
 * "stopped" can never be overridden by a stale "still recording" SSE read,
 * which is the exact bug this exists to fix.
 */
export function recordingStatusLabel(status: RecordingStatus | null, fallbackRecording: boolean): string {
  if (status === 'recording') return 'Recording';
  if (status === 'recovering') return 'Recording stopped — uploads recovering';
  if (status === 'complete') return 'Media complete';
  return fallbackRecording ? 'recording' : 'idle';
}

/**
 * A plain-language note for the "recovering" state — explains why video may
 * still be missing, instead of leaving a viewer to wonder. Null for every
 * other status (nothing extra to say).
 */
export function recordingRecoveringNote(status: RecordingStatus | null): string | null {
  return status === 'recovering' ? 'Some video may be missing until uploads finish.' : null;
}
